import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/auth";
import { useNotification } from "../../context/notificationcontext";
import axios from "axios";
import Header from "../../components/header/Header";
import "./Notifications.css";

const NotificationScreen = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { notification } = useNotification();

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const userId = user?._id;

  const fetchNotifications = async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/notifications/${userId}`);
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setError("Failed to load notifications");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (notifications.length === 0 || !userId) return;

    const timer = setTimeout(async () => {
      if (notifications.some((notif) => !notif.read)) {
        try {
          const response = await axios({
            method: "put",
            url: "/notifications/mark-all-read",
            data: { userId },
            headers: {
              "Content-Type": "application/json",
            },
          });

          setNotifications((prev) =>
            prev.map((notif) => ({ ...notif, read: true }))
          );
        } catch (error) {
          console.error("Error marking notifications as read:", error);
        }
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [notifications, userId]);

  useEffect(() => {
    if (userId) {
      fetchNotifications();
    }
  }, [userId]);

  const groupNotificationsByTime = (notifications) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    startOfWeek.setDate(today.getDate() - dayOfWeek);

    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfWeek.getDate() - 7);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    const groups = {
      new: [],
      earlierToday: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      old: [],
    };

    for (let i = 2; i < dayOfWeek; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayName = dayNames[date.getDay()];
      groups[dayName.toLowerCase()] = [];
    }

    notifications.forEach((notification) => {
      const notifDate = new Date(notification.createdAt);
      const notifDateOnly = new Date(
        notifDate.getFullYear(),
        notifDate.getMonth(),
        notifDate.getDate()
      );
      const hoursSinceCreation =
        (now.getTime() - notifDate.getTime()) / (1000 * 60 * 60);

      if (hoursSinceCreation <= 1) {
        groups.new.push(notification);
      } else if (notifDateOnly.getTime() === today.getTime()) {
        groups.earlierToday.push(notification);
      } else if (notifDateOnly.getTime() === yesterday.getTime()) {
        groups.yesterday.push(notification);
      } else if (notifDateOnly >= startOfWeek && notifDateOnly < yesterday) {
        const dayName = dayNames[notifDate.getDay()].toLowerCase();
        if (groups[dayName]) {
          groups[dayName].push(notification);
        }
      } else if (
        notifDateOnly >= startOfLastWeek &&
        notifDateOnly < startOfWeek
      ) {
        groups.lastWeek.push(notification);
      } else if (
        notifDateOnly >= startOfLastMonth &&
        notifDateOnly < startOfMonth
      ) {
        groups.lastMonth.push(notification);
      } else {
        groups.old.push(notification);
      }
    });

    return groups;
  };

  const formatNotificationDate = (dateString) => {
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return {
      month: months[date.getMonth()],
      day: date.getDate().toString(),
    };
  };

  const getDayOrderForCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const dayNames = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    const orderedDays = [];
    for (let i = 2; i < dayOfWeek; i++) {
      const dayIndex = (dayOfWeek - i + 7) % 7;
      orderedDays.push(dayNames[dayIndex]);
    }

    return orderedDays;
  };

  const renderDateBadge = (dateString) => {
    const { month, day } = formatNotificationDate(dateString);
    return (
      <div className="date-badge">
        <div className="date-month">{month}</div>
        <div className="date-day">{day}</div>
      </div>
    );
  };

  const renderNotificationCard = (notification) => {
    const notificationType = notification.data?.type || "event";

    if (notificationType === "welcome" || notificationType === "update") {
      return renderWelcomeNotification(notification);
    }
    return renderEventNotification(notification);
  };

  const renderWelcomeNotification = (notification) => (
    <div key={`welcome-${notification._id}`} className="welcome-card">
      {renderDateBadge(notification.createdAt)}
      <div className="welcome-text-container">
        <h3 className="welcome-title">{notification.title}</h3>
        <p className="welcome-message">{notification.body}</p>
      </div>
    </div>
  );

  const renderEventNotification = (notification) => (
    <div key={`event-${notification._id}`} className="notification-card">
      <div className="notification-content">
        <div className="guest-avatars">
          {renderDateBadge(notification.createdAt)}
        </div>
        <div className="notification-text">
          <h3 className="notification-title">{notification.title}</h3>
          <p className="notification-message">{notification.body}</p>
        </div>
      </div>
    </div>
  );

  const ComingSoonView = () => (
    <div className="coming-soon-container">
      <div className="coming-soon-content">
        <h2 className="coming-soon-title">No Notifications</h2>
      </div>
    </div>
  );

  const LoadingView = () => (
    <div className="coming-soon-container">
      <div className="spinner"></div>
      <h2 className="coming-soon-title">Loading notifications...</h2>
    </div>
  );

  const ErrorView = () => (
    <div className="coming-soon-container">
      <h2 className="coming-soon-title">Failed to load notifications</h2>
      <button onClick={fetchNotifications} className="retry-button">
        Retry
      </button>
    </div>
  );

  const groupedNotifications = groupNotificationsByTime(notifications);

  return (
    <div className="notification-screen-container">
      <Header />

      <div className="notification-header">
        <h1 className="notification-title">Notifications</h1>
      </div>

      {isLoading ? (
        <LoadingView />
      ) : error ? (
        <ErrorView />
      ) : notifications.length === 0 ? (
        <ComingSoonView />
      ) : (
        <div className="scroll-view">
          {groupedNotifications.new.length > 0 && (
            <div className="section">
              <h2 className="section-title-new">New</h2>
              {groupedNotifications.new.map(renderNotificationCard)}
            </div>
          )}

          {groupedNotifications.earlierToday.length > 0 && (
            <div className="section">
              <h2 className="section-title">Earlier Today</h2>
              {groupedNotifications.earlierToday.map(renderNotificationCard)}
            </div>
          )}

          {groupedNotifications.yesterday.length > 0 && (
            <div className="section">
              <h2 className="section-title">Yesterday</h2>
              {groupedNotifications.yesterday.map(renderNotificationCard)}
            </div>
          )}

          {getDayOrderForCurrentWeek().map((day) => {
            const dayNotifications = groupedNotifications[day];
            if (dayNotifications && dayNotifications.length > 0) {
              return (
                <div key={day} className="section">
                  <h2 className="section-title">
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </h2>
                  {dayNotifications.map(renderNotificationCard)}
                </div>
              );
            }
            return null;
          })}

          {groupedNotifications.lastWeek.length > 0 && (
            <div className="section">
              <h2 className="section-title">Last Week</h2>
              {groupedNotifications.lastWeek.map(renderNotificationCard)}
            </div>
          )}

          {groupedNotifications.lastMonth.length > 0 && (
            <div className="section">
              <h2 className="section-title">Last Month</h2>
              {groupedNotifications.lastMonth.map(renderNotificationCard)}
            </div>
          )}

          {groupedNotifications.old.length > 0 && (
            <div className="section">
              <h2 className="section-title">Old</h2>
              {groupedNotifications.old.map(renderNotificationCard)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationScreen;
